// ProductionProcessMobile.jsx — "Gérer la production" en version mobile (parcours guidé)
// À copier dans : src/GestionProductions/ProductionProcessMobile.jsx
//
// Câblé sur le MÊME backend que ProductionProcess.js :
//   getProductionUnits · getProductionUnitById · calculateProductionNeeds · processProduction (./api)
// Contextes réels : useAuth, useSelection. Terminologie : getProductionTerminology.
// La faisabilité vient de calc.all_available ; chaque intrant de materials[].is_sufficient.
// Le calculateur inverse autoritaire est partagé avec l'écran desktop.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { message, Modal } from "antd";
import {
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router-dom";
import {
  RocketOutlined, CheckOutlined, MinusOutlined, PlusOutlined, RightOutlined,
  SearchOutlined, BellOutlined, CloseOutlined, ShoppingCartOutlined,
  LoadingOutlined, ArrowDownOutlined, ArrowUpOutlined, InboxOutlined, ExperimentOutlined,
  AppstoreOutlined, WarningFilled, ExclamationCircleFilled, CalculatorOutlined,
} from "@ant-design/icons";
import {
  getProductionUnits, getProductionUnitById, calculateProductionNeeds, processProduction,
} from "./api";
import { useAuth } from "../context/AuthContext";
import { useSelection } from "../SelectionContext";
import { getSelectedProductionTerminology } from "./productionTerminology";
import ProductionCapacityCalculator from "./ProductionCapacityCalculator";
import MobileModuleHeader, { MobileHeaderAction } from "../mobile/MobileModuleHeader";
import StockShortageActionsModal from "../components/StockShortageActionsModal";
import {
  buildMissingStockItems,
  buildSalesProductionReplenishmentNavigation,
  buildStockReplenishmentRequest,
  buildStockReplenishmentReturnNavigation,
  getSalesProductionReplenishmentStep,
  isSalesProductionReplenishment,
} from "../utils/stockReplenishment";
import "./production-mobile-kit.css";

const MANAGE_CREATE = "Gestion Commerciale.Approvisionnement.Production.Gerer.create";
const PURCHASE_VIEW = "Gestion Commerciale.Approvisionnement.Achats.Achat.view";
const PURCHASE_CREATE = "Gestion Commerciale.Approvisionnement.Achats.Achat.create";
const ADJUST_VIEW = "Gestion Commerciale.Stock.GestionStock.Adjust.view";
const ADJUST_CREATE = "Gestion Commerciale.Stock.GestionStock.Adjust.create";
const SEMI = "semi_finished";
const MAX_PRODUCTION_OUTPUT_QUANTITY = 999999.99;

const fmtNum = (v, d = 0) =>
  new Intl.NumberFormat("fr-FR", { maximumFractionDigits: d }).format(Number(v) || 0);
const fmtF = (v) => fmtNum(Math.round(Number(v) || 0)) + " F";
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

// statut visuel d'une jauge à partir des champs backend
function gaugeOf(m) {
  const need = num(m.required_quantity);
  const stock = num(m.available);
  const pct = stock > 0 ? (need / stock) * 100 : (need > 0 ? 100 : 0);
  if (!m.is_sufficient) return { pct: 100, cls: "is-short" };
  if (pct >= 85) return { pct, cls: "is-tight" };
  return { pct: Math.min(100, pct), cls: "is-ok" };
}

// Indication immédiate dérivée de la recette brute. Le calculateur serveur
// reste autoritaire et la revalide avant le lancement.
function maxProducible(unit, calc) {
  if (!unit?.materials?.length) return null;
  const baseOutput = num(unit?.outputs?.[0]?.quantity) || 1;
  const latestMaterialsByProductId = new Map(
    (calc?.materials || []).map((material) => [
      Number(material.product_id),
      material,
    ])
  );
  const recipeQuantityByProductId = new Map();

  for (const material of unit.materials) {
    const productId = Number(material.product_id);
    if (!(productId > 0)) continue;
    if (recipeQuantityByProductId.has(productId)) {
      return null;
    }
    recipeQuantityByProductId.set(productId, {
      material,
      recipeQuantity: num(material.quantity),
    });
  }

  let max = Infinity;
  for (const [productId, recipe] of recipeQuantityByProductId.entries()) {
    const { material, recipeQuantity } = recipe;
    if (recipeQuantity <= 0) continue;
    const latestMaterial = latestMaterialsByProductId.get(productId);
    const stock = Math.max(
      num(
        latestMaterial?.available ??
          latestMaterial?.current_stock ??
          material.current_stock ??
          material.available
      ),
      0
    );
    const usableStock = Math.floor((stock + 1e-9) * 100) / 100;
    max = Math.min(max, (usableStock * baseOutput) / recipeQuantity);
  }
  if (!Number.isFinite(max)) return null;
  const roundedMax = Math.floor((max + 1e-9) * 100) / 100;
  return Math.min(roundedMax, MAX_PRODUCTION_OUTPUT_QUANTITY);
}

export default function ProductionProcessMobile({ onBack, restaurantMode = false }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const outletContext = useOutletContext();
  const { hasPermission, user } = useAuth();
  const { selectedWarehouse, selectedCompany } = useSelection();
  const t = getSelectedProductionTerminology(user, selectedCompany);
  const canLaunch = hasPermission(MANAGE_CREATE);
  const canPurchase = hasPermission(PURCHASE_VIEW) && hasPermission(PURCHASE_CREATE);
  const canAdjust = hasPermission(ADJUST_VIEW) && hasPermission(ADJUST_CREATE);
  const stockReplenishmentReturnContext = useMemo(() => {
    const context = location.state?.stockReplenishmentReturn;
    const productionUnitId = num(context?.production_unit_id);
    if (!context || !(productionUnitId > 0)) return null;

    return {
      ...context,
      production_unit_id: productionUnitId,
      output_quantity: num(context.output_quantity),
    };
  }, [location.state]);
  const salesProductionReplenishmentContext = useMemo(() => {
    const context = location.state?.salesProductionReplenishment;
    if (!isSalesProductionReplenishment(context)) return null;

    const selectedCompanyId = Number(selectedCompany?.id ?? selectedCompany);
    const selectedWarehouseId = Number(
      selectedWarehouse?.id ?? selectedWarehouse
    );
    const scopeMatches =
      Number(context.user_id) === Number(user?.id) &&
      Number(context.company_id) === selectedCompanyId &&
      Number(context.warehouse_id) === selectedWarehouseId;
    return scopeMatches ? context : null;
  }, [location.state, selectedCompany, selectedWarehouse, user?.id]);
  const salesProductionStep = useMemo(
    () =>
      getSalesProductionReplenishmentStep(
        salesProductionReplenishmentContext
      ),
    [salesProductionReplenishmentContext]
  );
  const clearStockReplenishmentReturn = () => {
    if (!location.state?.stockReplenishmentReturn) return;
    const nextState = { ...(location.state || {}) };
    delete nextState.stockReplenishmentReturn;
    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
      },
      {
        replace: true,
        state: Object.keys(nextState).length > 0 ? nextState : null,
      }
    );
  };

  const [units, setUnits] = useState([]);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [unit, setUnit] = useState(null);          // unité sélectionnée (détail)
  const [qty, setQty] = useState(1);
  const [calc, setCalc] = useState(null);
  const [calcLoading, setCalcLoading] = useState(false);
  const [sheet, setSheet] = useState(null);        // null | 'picker' | 'success'
  const [result, setResult] = useState(null);
  const [producing, setProducing] = useState(false);
  const [editingQty, setEditingQty] = useState(false);
  const [capacityCalculatorOpen, setCapacityCalculatorOpen] = useState(false);
  const [shortageActionsOpen, setShortageActionsOpen] = useState(false);
  const [unitQuery, setUnitQuery] = useState("");
  const unitSearchRef = useRef(null);

  // unités filtrées par la recherche de la feuille de sélection
  const visibleUnits = useMemo(() => {
    const q = unitQuery.trim().toLowerCase();
    if (!q) return units;
    return units.filter((u) => String(u.name || "").toLowerCase().includes(q));
  }, [units, unitQuery]);

  const openPicker = ({ focusSearch = false } = {}) => {
    setUnitQuery("");
    setSheet("picker");
    // le champ n'existe qu'après le rendu de la feuille
    if (focusSearch) requestAnimationFrame(() => unitSearchRef.current?.focus());
  };
  const closePicker = () => { setUnitQuery(""); setSheet(null); };

  const warehouseName = useMemo(() => {
    const w = (user?.assigned_warehouses || []).find((w) => w.id === selectedWarehouse);
    return w ? w.name : null;
  }, [user, selectedWarehouse]);

  // -- charger les unités du magasin --
  useEffect(() => {
    let alive = true;
    if (!selectedWarehouse) { setUnits([]); return; }
    setLoadingUnits(true);
    getProductionUnits({ page: 1, limit: 100, warehouse_id: selectedWarehouse, company_id: selectedCompany })
      .then((data) => {
        if (!alive) return;
        const list = (data?.units || []).filter(
          (u) => Number(u.warehouse_id) === Number(selectedWarehouse)
        );
        setUnits(list);
      })
      .catch(() => alive && message.error(`Erreur lors du chargement des ${t.unitPlural}`))
      .finally(() => alive && setLoadingUnits(false));
    return () => { alive = false; };
  }, [selectedWarehouse, selectedCompany]); // eslint-disable-line

  // -- recalcul (déclenché par qty ou unité), avec léger debounce --
  const debounce = useRef(null);
  const runCalculate = (unitId, quantity) => {
    if (!unitId || !selectedWarehouse || !(quantity > 0)) return;
    setCalcLoading(true);
    calculateProductionNeeds({
      production_unit_id: unitId,
      output_quantity: quantity,
      warehouse_id: selectedWarehouse,
      company_id: selectedCompany,
    })
      .then((data) => setCalc(data))
      .catch((e) => { setCalc(null); message.error(e?.error || e?.message || "Erreur lors du calcul des besoins"); })
      .finally(() => setCalcLoading(false));
  };
  const scheduleCalculate = (unitId, quantity) => {
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => runCalculate(unitId, quantity), 350);
  };

  const pickUnit = async (unitId, { outputQuantityOverride } = {}) => {
    closePicker();
    try {
      const data = await getProductionUnitById(unitId, { company_id: selectedCompany, warehouse_id: selectedWarehouse });
      if (Number(data.warehouse_id) !== Number(selectedWarehouse)) {
        return message.error(`Cette ${t.unitSingular} n'appartient pas à l'établissement sélectionné`);
      }
      const restoredQty = num(outputQuantityOverride);
      const defQty = restoredQty > 0
        ? restoredQty
        : num(data?.outputs?.[0]?.quantity) > 0
          ? num(data.outputs[0].quantity)
          : 1;
      setUnit(data); setQty(defQty); setCalc(null);
      runCalculate(unitId, defQty);
    } catch {
      message.error(`Erreur lors du chargement de ${t.unitSingular}`);
    }
  };

  useEffect(() => {
    const requestedUnitId =
      salesProductionStep?.item?.production_unit_id ||
      stockReplenishmentReturnContext?.production_unit_id ||
      Number(id);
    if (requestedUnitId > 0 && selectedWarehouse) {
      pickUnit(requestedUnitId, {
        outputQuantityOverride:
          salesProductionStep?.item?.output_quantity ||
          stockReplenishmentReturnContext?.output_quantity,
      });
    }
  }, [
    id,
    selectedCompany,
    selectedWarehouse,
    stockReplenishmentReturnContext?.production_unit_id,
    stockReplenishmentReturnContext?.output_quantity,
    salesProductionStep?.item?.production_unit_id,
    salesProductionStep?.item?.output_quantity,
  ]); // eslint-disable-line

  const changeQty = (next) => {
    const q = Math.min(
      MAX_PRODUCTION_OUTPUT_QUANTITY,
      Math.max(0.01, Number(next) || 0.01)
    );
    setQty(q);
    if (unit) scheduleCalculate(unit.id, q);
  };

  const cs = calc?.cost_summary || null;
  const feasible = Boolean(calc?.all_available);
  const max = useMemo(() => maxProducible(unit, calc), [unit, calc]);
  const shortCount = (calc?.materials || []).filter((m) => !m.is_sufficient).length;
  const outShort = unit?.outputs?.[0]?.unit_short_name || "";
  const guardrails = calc?.pricing_guardrails || null;
  const blocked = guardrails?.status === "blocked";
  const missingStockItems = useMemo(
    () => buildMissingStockItems(calc?.materials),
    [calc?.materials]
  );

  const openShortageActions = () => {
    if (!missingStockItems.length) {
      return message.info("Aucun stock manquant n'a été détecté.");
    }
    if (!canPurchase && !canAdjust) {
      return message.error(
        "Vous n'avez pas la permission de créer un achat ou un ajustement de stock."
      );
    }
    setShortageActionsOpen(true);
  };

  const resolveShortage = (method) => {
    if (method === "purchase" && !canPurchase) {
      return message.error("Vous n'avez pas la permission de créer un achat.");
    }
    if (method === "adjustment" && !canAdjust) {
      return message.error(
        "Vous n'avez pas la permission de créer un ajustement de stock."
      );
    }

    const replenishment = buildStockReplenishmentRequest({
      sourceType: "production",
      sourceLabel: unit?.name || "production",
      sourcePath: `${location.pathname}${location.search}`,
      companyId: selectedCompany,
      warehouseId: selectedWarehouse,
      items: missingStockItems,
      metadata: {
        production_unit_id: unit?.id || null,
        production_unit_name: unit?.name || "",
        output_quantity: qty,
        return_context: {
          production_unit_id: unit?.id || null,
          output_quantity: qty,
          restaurant_mode: restaurantMode,
          restaurant_ticket: null,
          sales_production_replenishment:
            salesProductionReplenishmentContext || null,
        },
      },
    });

    setShortageActionsOpen(false);
    navigate(
      method === "purchase"
        ? "/gestion-achats/achat"
        : "/gestion-stock?tab=adjustments",
      { state: { stockReplenishment: replenishment } }
    );
  };

  // -- lancement --
  const doLaunch = async (acknowledged = false) => {
    setProducing(true);
    try {
      const payload = {
        production_unit_id: unit.id,
        output_quantity: qty,
        user_id: user?.id || null,
        warehouse_id: selectedWarehouse,
        company_id: selectedCompany,
        notes: `${t.processTitle} de ${qty} unités`,
        recipe_version_hash: calc?.recipe_summary?.version_hash || null,
        pricing_guard_acknowledged: acknowledged,
        __offline_preview: {
          production_unit_id: unit.id,
          production_unit_name: unit.name,
          production_kind: unit.production_kind || "finished",
          output_quantity: qty,
          user_name: user?.name || "Utilisateur",
          cost_summary: {
            total_material_cost: num(cs?.estimated_material_cost),
            total_variable_cost: num(cs?.total_variable_cost),
            total_cost: num(cs?.estimated_total_cost),
            unit_cost_real: num(cs?.estimated_unit_cost),
          },
          materials: calc?.materials || [],
          outputs: calc?.outputs || [],
          variable_costs: calc?.variable_costs || [],
          financial_tracking: calc?.financial_tracking || null,
          offline_snapshot: calc?.offline_snapshot || null,
        },
      };
      const res = await processProduction(payload);
      clearStockReplenishmentReturn();
      setResult({
        code: res?.production_code || null,
        name: unit.name,
        qtyLabel: `${fmtNum(qty, 2)} ${outShort}`.trim(),
        cost: num(res?.cost_summary?.total_cost ?? cs?.estimated_total_cost),
        unit: num(res?.cost_summary?.unit_cost_real ?? cs?.estimated_unit_cost),
        financial: res?.financial_tracking || calc?.financial_tracking || null,
        salesProductionReplenishment:
          salesProductionReplenishmentContext || null,
      });
      setSheet("success");
    } catch (e) {
      message.error(e?.response?.data?.error || e?.error || `Erreur lors de la ${t.costContext}`);
    } finally {
      setProducing(false);
    }
  };

  const handleLaunch = () => {
    if (!canLaunch) return message.error(`Vous n'avez pas la permission de démarrer la ${t.costContext}.`);
    if (!feasible) return;
    if (blocked) return message.error(guardrails?.user_guidance?.title || "Lancement bloqué tant que les points signalés ne sont pas traités.");
    if (guardrails?.requires_acknowledgement) {
      Modal.confirm({
        title: "Confirmation requise", okText: "Confirmer et lancer", cancelText: "Annuler", centered: true,
        content: guardrails?.user_guidance?.summary || "Une vérification est demandée avant de lancer cette production.",
        onOk: () => doLaunch(true),
      });
      return;
    }
    doLaunch(false);
  };

  const resetAfterSuccess = () => {
    setSheet(null); setCalc(null); setUnit(null); setQty(1); setResult(null);
  };

  const returnToSales = (replenishment) => {
    const navigation = buildStockReplenishmentReturnNavigation(
      replenishment,
      "production"
    );
    if (!navigation) {
      return message.error(
        "Impossible de retrouver le panier de vente d’origine."
      );
    }
    setSheet(null);
    navigate(navigation.path, { state: navigation.state });
  };

  const continueSalesProduction = (replenishment) => {
    const step = getSalesProductionReplenishmentStep(replenishment);
    const navigation = step?.has_next
      ? buildSalesProductionReplenishmentNavigation(
          replenishment,
          step.current_index + 1
        )
      : null;
    if (!navigation) {
      returnToSales(replenishment);
      return;
    }
    setSheet(null);
    navigate(navigation.path, { state: navigation.state });
  };

  const resultSalesProductionReplenishment =
    result?.salesProductionReplenishment || null;
  const resultSalesProductionStep = getSalesProductionReplenishmentStep(
    resultSalesProductionReplenishment
  );
  const handleBack = salesProductionReplenishmentContext
    ? () => returnToSales(salesProductionReplenishmentContext)
    : onBack;

  /* ---------- garde : aucun magasin ---------- */
  if (!selectedWarehouse) {
    return (
      <div className="pm-root">
        <Header
          t={t}
          onBack={handleBack}
          onMenu={
            salesProductionReplenishmentContext
              ? undefined
              : outletContext?.openMobileMenu
          }
          restaurantMode={restaurantMode}
          warehouseName={null}
          onBell={() => navigate("/notifications")}
        />
        <div className="pm-body">
          <div className="pm-state">
            <div className="pm-state-badge"><ExclamationCircleFilled /></div>
            <h3 className="pm-state-title">Aucun établissement sélectionné</h3>
            <p className="pm-state-text">Choisissez un établissement dans la barre de navigation pour gérer la {t.costContext}.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pm-root">
      <Header
        t={t}
        onBack={handleBack}
        onMenu={
          salesProductionReplenishmentContext
            ? undefined
            : outletContext?.openMobileMenu
        }
        restaurantMode={restaurantMode}
        warehouseName={warehouseName}
        onSearch={() => openPicker({ focusSearch: true })}
        onBell={() => navigate("/notifications")}
      />

      <div className="pm-body">
        {salesProductionStep && (
          <div className="pm-callout pm-callout--blue">
            <div className="pm-callout-ico">
              <ExperimentOutlined />
            </div>
            <div>
              <strong>
                Réapprovisionnement du panier · étape {salesProductionStep.current_index + 1}/{salesProductionStep.total}
              </strong>
              <p>
                Préparez {fmtNum(salesProductionStep.item.output_quantity, 5)} {salesProductionStep.item.unit_short_name || salesProductionStep.item.unit_name || "unité(s)"} de {salesProductionStep.item.product_name || "ce produit"}.
              </p>
            </div>
          </div>
        )}

        {stockReplenishmentReturnContext && (
          <div
            className={`pm-callout ${
              !calc
                ? "pm-callout--blue"
                : feasible
                  ? "pm-callout--green"
                  : "pm-callout--amber"
            }`}
          >
            <div className="pm-callout-ico">
              {!calc ? (
                <LoadingOutlined />
              ) : feasible ? (
                <CheckOutlined />
              ) : (
                <ExclamationCircleFilled />
              )}
            </div>
            <div style={{ flex: 1 }}>
              <div className="pm-callout-title">
                {!calc
                  ? "Stock réapprovisionné — recalcul en cours"
                  : feasible
                    ? "Stock désormais suffisant"
                    : "Réapprovisionnement encore insuffisant"}
              </div>
              <div className="pm-callout-sub">
                {!calc
                  ? `La ${t.unitSingular} et la quantité ont été restaurées.`
                  : feasible
                    ? `Vérifiez le résultat puis confirmez manuellement le lancement de la ${t.costContext}.`
                    : "Les quantités encore manquantes sont indiquées ci-dessous."}
              </div>
            </div>
          </div>
        )}

        {/* sélection recette */}
        {unit ? (
          <div className="pm-recipe">
            <div className="pm-iconchip" style={{ width: 52, height: 52, borderRadius: 14, fontSize: 24 }}>
              {unit.production_kind === SEMI ? <AppstoreOutlined /> : <ExperimentOutlined />}
            </div>
            <div className="pm-recipe-mid">
              <div className="pm-recipe-name">{unit.name}</div>
              <div className="pm-recipe-meta">
                <KindPill kind={unit.production_kind} />
                {unit.recipe_summary?.version_label && <span className="pm-chip">{unit.recipe_summary.version_label}</span>}
              </div>
            </div>
            <button className="pm-recipe-swap" onClick={() => openPicker()}>Changer<RightOutlined /></button>
          </div>
        ) : (
          <button className="pm-recipe" style={{ width: "100%", textAlign: "left" }} onClick={() => openPicker()}>
            <div className="pm-iconchip" style={{ width: 52, height: 52, borderRadius: 14, fontSize: 24 }}><ExperimentOutlined /></div>
            <div className="pm-recipe-mid">
              <div className="pm-recipe-name">Choisir une {t.unitSingular}</div>
              <div className="pm-sub" style={{ marginTop: 4 }}>{units.length} {t.unitPlural} disponibles</div>
            </div>
            <span className="pm-recipe-swap"><RightOutlined /></span>
          </button>
        )}

        {unit && (
          <>
            {/* quantité */}
            <div className="pm-qty">
              <div className="pm-block-title">{t.outputQuantityLabel}</div>
              <div className="pm-stepper">
                <button className="pm-step-btn" onClick={() => changeQty(qty - 1)} disabled={qty <= 0.01}><MinusOutlined /></button>
                <div className="pm-step-val" onClick={() => setEditingQty(true)}>
                  {editingQty ? (
                    <input autoFocus type="number" inputMode="decimal" className="pm-step-num"
                      style={{ width: 140, textAlign: "center", border: "none", outline: "none", background: "transparent" }}
                      defaultValue={qty}
                      onBlur={(e) => { setEditingQty(false); changeQty(Math.max(0.01, Number(e.target.value) || 0.01)); }}
                      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }} />
                  ) : (
                    <>
                      <span className="pm-step-num" style={!feasible && calc ? { color: "var(--pm-red-d)" } : null}>{fmtNum(qty, 2)}</span>
                      <span className="pm-step-unit">{outShort}</span>
                    </>
                  )}
                </div>
                <button className="pm-step-btn" onClick={() => changeQty(qty + 1)}><PlusOutlined /></button>
              </div>
              {max != null && (
                <div className="pm-qty-hint">Stock disponible pour <b>{fmtNum(max, 2)} {outShort}</b> maximum par lancement</div>
              )}
              <button
                className="pm-btn pm-btn--ghost pm-btn--sm"
                style={{ marginTop: 10 }}
                onClick={() => setCapacityCalculatorOpen(true)}
              >
                <CalculatorOutlined />
                Calculer selon mes {t.materialPlural}
              </button>
            </div>

            {/* verdict */}
            {calc && (
              feasible ? (
                <div className="pm-verdict pm-verdict--ok">
                  <div className="pm-verdict-ico"><CheckOutlined /></div>
                  <div>
                    <div className="pm-verdict-title">Production réalisable</div>
                    <div className="pm-verdict-sub">Les {calc.materials.length} {t.materialPlural} sont disponibles en stock</div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="pm-verdict pm-verdict--bad">
                    <div className="pm-verdict-ico"><WarningFilled /></div>
                    <div>
                      <div className="pm-verdict-title">Stock insuffisant</div>
                      <div className="pm-verdict-sub">{shortCount} {shortCount > 1 ? t.materialPlural : t.materialSingular} en manque pour {fmtNum(qty, 2)} {outShort}</div>
                    </div>
                  </div>
                  {max != null && max > 0 && (
                    <div className="pm-callout pm-callout--amber">
                      <div className="pm-callout-ico"><ExclamationCircleFilled /></div>
                      <div style={{ flex: 1 }}>
                        <div className="pm-callout-title">Production maximale possible : {fmtNum(max, 2)} {outShort}</div>
                        <div className="pm-callout-sub">Ajustez la quantité pour lancer dès maintenant avec le stock actuel.</div>
                        <div className="pm-callout-actions">
                          <button className="pm-btn pm-btn--primary pm-btn--sm" onClick={() => changeQty(max)}>Ajuster à {fmtNum(max, 2)} {outShort}</button>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )
            )}

            {Number(calc?.financial_tracking?.pending_regularization_count) >
              0 && (
              <div className="pm-callout pm-callout--amber">
                <div className="pm-callout-ico">
                  <ExclamationCircleFilled />
                </div>
                <div style={{ flex: 1 }}>
                  <div className="pm-callout-title">
                    Production autorisée avec avertissement
                  </div>
                  <div className="pm-callout-sub">
                    {calc.financial_tracking.warning ||
                      "Les coûts variables incomplets restent inclus dans le coût de production, mais leurs dépenses devront être régularisées depuis l'historique."}
                  </div>
                </div>
              </div>
            )}

            {/* intrants */}
            {calc?.materials?.length > 0 && (
              <div className="pm-card">
                <div className="pm-block-head">
                  <span className="pm-block-title">{t.consumedMaterialsTitle}</span>
                  {feasible
                    ? <span className="pm-chip">{calc.materials.length}</span>
                    : <span className="pm-pill pm-pill--red pm-pill--sm">{shortCount} insuffisants</span>}
                </div>
                {calc.materials.map((m, i) => (
                  <IngredientRow
                    key={m.id ?? m.product_id ?? i}
                    m={m}
                    t={t}
                    onResolveStock={openShortageActions}
                  />
                ))}
              </div>
            )}

            {/* coûts */}
            {cs && (
              <div className="pm-card">
                <div className="pm-block-head"><span className="pm-block-title">Coûts de {t.costContext}</span></div>
                <div className="pm-cost-row"><span className="lbl"><InboxOutlined style={{ color: "var(--pm-muted)" }} />Coût des {t.materialPlural}</span><span className="val">{fmtF(cs.estimated_material_cost)}</span></div>
                <div className="pm-cost-div" />
                <div className="pm-cost-row"><span className="lbl"><ExperimentOutlined style={{ color: "var(--pm-muted)" }} />Coûts variables</span><span className="val">{fmtF(cs.total_variable_cost)}</span></div>
                <div className="pm-cost-div" />
                <div className="pm-cost-total"><span className="lbl">Coût total prévu</span><span className="val">{fmtF(cs.estimated_total_cost)}</span></div>
                <div className="pm-unitcost">
                  <span className="pm-unitcost-l">Coût unitaire de revient</span>
                  <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    {Number.isFinite(num(cs.delta_ratio)) && cs.delta_ratio !== 0 && (
                      <span className={`pm-pill pm-pill--sm ${num(cs.delta_ratio) <= 0 ? "pm-pill--green" : "pm-pill--amber"}`}>
                        {num(cs.delta_ratio) <= 0 ? <ArrowDownOutlined /> : <ArrowUpOutlined />}{fmtNum(Math.abs(num(cs.delta_ratio) * 100), 1)} %
                      </span>
                    )}
                    <span className="pm-unitcost-v">{fmtF(cs.estimated_unit_cost)}</span>
                  </span>
                </div>
              </div>
            )}

            {calcLoading && !calc && <div className="pm-spin"><LoadingOutlined /></div>}
            <div style={{ height: 8 }} />
          </>
        )}
      </div>

      {/* barre d'action */}
      {unit && calc && (
        <div className="pm-actionbar">
          {feasible ? (
            <div className="pm-actionbar-row">
              <div>
                <div className="pm-actiontotal-l">Coût total</div>
                <div className="pm-actiontotal-v">{fmtF(cs?.estimated_total_cost)}</div>
              </div>
              <button className="pm-btn pm-btn--green" disabled={!canLaunch || blocked || producing} onClick={handleLaunch}>
                {producing ? <LoadingOutlined /> : <RocketOutlined />}{t.launchAction}
              </button>
            </div>
          ) : (
            <button
              className="pm-btn pm-btn--primary pm-btn--block"
              onClick={openShortageActions}
            >
              <ShoppingCartOutlined />Approvisionner les {shortCount} {t.materialPlural} manquants
            </button>
          )}
        </div>
      )}

      {/* feuille : choix de recette */}
      {sheet === "picker" && (
        <>
          <div className="pm-scrim" onClick={closePicker} />
          <div className="pm-sheet">
            <div className="pm-grab" />
            <div className="pm-sheet-head">
              <div>
                <h2 className="pm-sheet-title">Choisir une {t.unitSingular}</h2>
                <p className="pm-sheet-sub">La production se calcule selon le stock de l'établissement</p>
              </div>
              <button className="pm-iconbtn" onClick={closePicker}><CloseOutlined /></button>
            </div>
            {units.length > 1 && (
              <div className="pm-search" style={{ marginBottom: 12 }}>
                <SearchOutlined />
                <input
                  ref={unitSearchRef}
                  value={unitQuery}
                  onChange={(e) => setUnitQuery(e.target.value)}
                  placeholder={`Rechercher une ${t.unitSingular}…`}
                />
                {unitQuery && (
                  <button className="pm-iconbtn" style={{ width: 28, height: 28 }} onClick={() => setUnitQuery("")}>
                    <CloseOutlined />
                  </button>
                )}
              </div>
            )}
            {loadingUnits ? (
              <div className="pm-spin"><LoadingOutlined /></div>
            ) : units.length === 0 ? (
              <div className="pm-state"><div className="pm-state-badge"><InboxOutlined /></div>
                <h3 className="pm-state-title">{t.noUnitFound}</h3>
                <p className="pm-state-text">Créez une {t.unitSingular} pour cet établissement avant de produire.</p></div>
            ) : visibleUnits.length === 0 ? (
              <div className="pm-state"><div className="pm-state-badge"><SearchOutlined /></div>
                <h3 className="pm-state-title">Aucun résultat</h3>
                <p className="pm-state-text">Aucune {t.unitSingular} ne correspond à « {unitQuery.trim()} ».</p></div>
            ) : (
              visibleUnits.map((u) => (
                <button key={u.id} className={`pm-unit-opt ${unit?.id === u.id ? "is-active" : ""}`} onClick={() => pickUnit(u.id)}>
                  <div className="pm-iconchip" style={{ width: 44, height: 44, fontSize: 20 }}>
                    {u.production_kind === SEMI ? <AppstoreOutlined /> : <ExperimentOutlined />}
                  </div>
                  <div className="pm-unit-opt-mid">
                    <div className="pm-unit-opt-name">{u.name}</div>
                    <div className="pm-unit-opt-meta"><KindPill kind={u.production_kind} /></div>
                  </div>
                  {unit?.id === u.id ? <CheckOutlined style={{ color: "var(--pm-primary)" }} /> : <RightOutlined style={{ color: "var(--pm-muted-2)" }} />}
                </button>
              ))
            )}
          </div>
        </>
      )}

      <ProductionCapacityCalculator
        open={capacityCalculatorOpen}
        onCancel={() => setCapacityCalculatorOpen(false)}
        onApply={(quantity) => {
          setCapacityCalculatorOpen(false);
          changeQty(quantity);
        }}
        unit={unit}
        companyId={selectedCompany}
        warehouseId={selectedWarehouse}
        terminology={t}
      />

      {/* feuille : succès */}
      {sheet === "success" && result && (
        <>
          <div className="pm-scrim" />
          <div className="pm-sheet pm-success">
            <div className="pm-success-burst"><CheckOutlined /></div>
            <div className="pm-success-title">{t.successMessage}</div>
            <div className="pm-success-sub">{result.qtyLabel} de {result.name} enregistrés en stock.</div>
            <div className="pm-success-card">
              {result.code && (<><div className="pm-success-row"><span>Lot de production</span><span className="pm-lot" style={{ fontSize: 13, padding: "4px 10px" }}>{result.code}</span></div><div className="pm-cost-div" /></>)}
              <div className="pm-success-row"><span>Coût total</span><b>{fmtF(result.cost)}</b></div>
              <div className="pm-success-row"><span>Coût unitaire réel</span><b>{fmtF(result.unit)}</b></div>
              {Number(result.financial?.expenses_created_count) > 0 && (
                <div className="pm-success-row">
                  <span>Dépenses non payées créées</span>
                  <b>{result.financial.expenses_created_count}</b>
                </div>
              )}
              {Number(result.financial?.pending_regularization_count) > 0 && (
                <div className="pm-success-row">
                  <span>Charges à régulariser</span>
                  <b>{result.financial.pending_regularization_count}</b>
                </div>
              )}
            </div>
            {resultSalesProductionReplenishment ? (
              <>
                {resultSalesProductionStep?.has_next && (
                  <button
                    className="pm-btn pm-btn--green pm-btn--block"
                    onClick={() =>
                      continueSalesProduction(
                        resultSalesProductionReplenishment
                      )
                    }
                  >
                    <RightOutlined /> Préparer le produit suivant
                  </button>
                )}
                <button
                  className={`pm-btn pm-btn--block ${
                    resultSalesProductionStep?.has_next
                      ? ""
                      : "pm-btn--green"
                  }`}
                  onClick={() =>
                    returnToSales(resultSalesProductionReplenishment)
                  }
                >
                  <CheckOutlined />
                  {resultSalesProductionReplenishment.source_type === "pos"
                    ? "Retourner au POS"
                    : "Retourner à la vente"}
                </button>
              </>
            ) : (
              <button className="pm-btn pm-btn--green pm-btn--block" onClick={resetAfterSuccess}><CheckOutlined />Terminer</button>
            )}
          </div>
        </>
      )}

      <StockShortageActionsModal
        open={shortageActionsOpen}
        title={`Approvisionner les ${t.materialPlural} manquants`}
        items={missingStockItems}
        canPurchase={canPurchase}
        canAdjust={canAdjust}
        onCancel={() => setShortageActionsOpen(false)}
        onPurchase={() => resolveShortage("purchase")}
        onAdjustment={() => resolveShortage("adjustment")}
      />
    </div>
  );
}

/* ---------- sous-composants ---------- */
function Header({ t, onBack, onMenu, restaurantMode, warehouseName, onSearch, onBell }) {
  return (
    <MobileModuleHeader
      title={t.manageTitle}
      subtitle={`Vérifiez la faisabilité puis lancez en une fois${restaurantMode ? " · Cuisine" : ""}`}
      onMenu={onMenu}
      onLeading={onMenu ? undefined : onBack}
      leadingIcon="arrow-left"
      leadingLabel="Retour"
      actions={
        <>
          {onSearch ? (
            <MobileHeaderAction label={`Rechercher une ${t.unitSingular}`} onClick={onSearch}>
              <SearchOutlined />
            </MobileHeaderAction>
          ) : null}
          <MobileHeaderAction label="Notifications" onClick={onBell}>
            <BellOutlined />
          </MobileHeaderAction>
        </>
      }
      context={warehouseName ? (
        <div className="pm-warehouse"><span className="pm-warehouse-dot" />{warehouseName}</div>
      ) : null}
    />
  );
}

function KindPill({ kind }) {
  const fin = kind !== SEMI;
  return (
    <span className={`pm-pill pm-pill--sm ${fin ? "pm-pill--blue" : "pm-pill--violet"}`}>
      {fin ? "Produit fini" : "Semi-fini"}
    </span>
  );
}

function IngredientRow({ m, t, onResolveStock }) {
  const g = gaugeOf(m);
  const need = num(m.required_quantity);
  const stock = num(m.available);
  const line = need * num(m.purchase_price);
  const shortfall = Math.max(0, need - stock);
  const lots = m.lot_allocations || [];
  return (
    <div className="pm-ing">
      <div className="pm-iconchip" style={{ width: 38, height: 38, borderRadius: 10, fontSize: 18,
        background: !m.is_sufficient ? "var(--pm-red-bg)" : undefined, color: !m.is_sufficient ? "var(--pm-red)" : undefined }}>
        <InboxOutlined />
      </div>
      <div className="pm-ing-mid">
        <div className="pm-ing-top">
          <span className="pm-ing-name">{m.product_name}</span>
          <span className="pm-ing-qty">
            <span className="need">{fmtNum(need, 2)} {m.unit_short_name}</span>
            <span className="sep">/</span>
            <span className="stock">{fmtNum(stock, 2)} dispo</span>
          </span>
        </div>
        <div className="pm-gauge"><div className={`pm-gauge-fill ${g.cls}`} style={{ width: g.pct + "%" }} /></div>
        <div className="pm-ing-foot">
          {m.is_sufficient ? (
            <span className="pm-ing-lots">
              {lots.slice(0, 1).map((l, i) => <span key={i} className="pm-lot">{l.lot_code || "Lot"}</span>)}
              {lots.length > 1 && <span className="pm-chip" style={{ fontSize: 11, padding: "2px 7px" }}>+{lots.length - 1}</span>}
            </span>
          ) : (
            <span className="pm-ing-short"><WarningFilled />Manque {fmtNum(shortfall, 2)} {m.unit_short_name}</span>
          )}
          {m.is_sufficient
            ? <span className="pm-ing-cost">{fmtF(line)}</span>
            : <button className="pm-btn pm-btn--ghost pm-btn--sm" onClick={onResolveStock}><ShoppingCartOutlined />Approvisionner</button>}
        </div>
      </div>
    </div>
  );
}
